import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { buildTrendData, withTrendTarget } from './shared.js';
import type { TrendView } from './shared.js';
import { TrendViewSwitch } from './TrendViewSwitch.js';
import type { IdtTeamKpi } from '../../../lib/api.js';

/** @graph-connects none */
const PLANNED_COLOR = '#38bdf8'; /* sky-400 */
/** @graph-connects none */
const ACTUAL_COLOR = 'var(--color-error)';
/** @graph-connects none */
const TARGET_COLOR = 'var(--color-idt-trend-target)';

/** 系列 dataKey → tooltip / 凡例の i18n key。 @graph-connects none */
const SERIES_LABEL_KEY: Record<string, string> = {
  planned: 'idtKpi.teamTrend.planned',
  target: 'idtKpi.teamTrend.target',
  actual: 'idtKpi.teamTrend.actual',
};

/**
 * tooltip 内の系列表示順。<Line> の描画順は planned 線を target 線の上へ重ねるため
 * target → planned だが、tooltip / 凡例は Planned → Target → Actual で揃える。
 * Recharts の Tooltip は itemSorter 未指定だと描画順に従うので、ここで明示的に並べ替える。
 * @graph-connects none
 */
const TOOLTIP_ORDER: Record<string, number> = { planned: 0, target: 1, actual: 2 };

/** chart 1 点分の系列値。date は quarter 日次 view のみ持つ (X 軸の月ラベル解決用)。 */
interface TrendPoint {
  day: number;
  date?: string;
  planned: number;
  actual: number | null;
}

/** view ごとの分岐で組み立てる chart 描画一式。 */
interface TrendMemo {
  data: TrendPoint[];
  today: number;
  ticks: number[];
  tickFormatter: (v: number) => string;
}

interface ActualDotProps {
  cx?: number;
  cy?: number;
  payload?: { day: number; actual: number | null };
}

/** @graph-connects none */
function renderActualLatestDot(today: number) {
  return (props: ActualDotProps) => {
    if (
      !props.payload ||
      props.payload.day !== today ||
      props.cx === undefined ||
      props.cy === undefined
    ) {
      return <g key="empty-dot" />;
    }
    const value = props.payload.actual ?? 0;
    return (
      <g key={`latest-${String(props.payload.day)}`}>
        <circle
          cx={props.cx}
          cy={props.cy}
          r={5}
          fill="var(--color-bg-surface)"
          stroke={ACTUAL_COLOR}
          strokeWidth={2.5}
        />
        <text
          x={props.cx}
          y={props.cy - 14}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={ACTUAL_COLOR}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value.toLocaleString()}
        </text>
      </g>
    );
  };
}

interface TeamTrendChartProps {
  /** "month" | "quarter" */
  view: TrendView;
  /** view 切替 callback */
  onViewChange: (next: TrendView) => void;
  /** ver.1: month view では実 API データ。quarter view は ver.2 で対応するため mock のまま */
  trend?: IdtTeamKpi;
  /** "YYYY-MM-DD" (JST 今日) */
  asOfDate?: string;
  /** 過去 Q view で実績を「完了のみ」で描く (処理中を含めない) */
  quarterCompletedOnly?: boolean;
}

/**
 * チーム合計の予定 vs 実績推移を見る折線 + Area chart。月 view では trendDaily を、quarter view では
 * quarterMonths を cumulative に積み上げて描画する。trend が無いときは mock データで描画する。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページ右上のチーム trend chart panel。月 / 四半期切替で planned/actual を可視化する
 * @graph-connects none
 */
export function TeamTrendChart({
  view,
  onViewChange,
  trend,
  asOfDate,
  quarterCompletedOnly = false,
}: TeamTrendChartProps) {
  const { t } = useTranslation();

  const { data, today, ticks, tickFormatter } = useMemo((): TrendMemo => {
    if (view === 'month' && trend && trend.trendDaily.length > 0) {
      // 実績は各日の累積 (actualCumulative) を線で描く。完了案件は完了更新日の日に、
      // 処理中案件は当日に計上済み。当日より後の日は null (線を引かない)。
      // dot/ReferenceLine 用の「今日」は最後に actual が non-null な日 (= 当日)。
      const arr = trend.trendDaily.map((d, i) => ({
        day: i + 1,
        planned: Math.round(d.plannedCumulative),
        actual: d.actualCumulative == null ? null : Math.round(d.actualCumulative),
      }));
      let asOfDay = arr.length;
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i]?.actual != null) {
          asOfDay = i + 1;
          break;
        }
      }
      const len = arr.length;
      return {
        data: arr,
        today: asOfDay,
        ticks: [1, Math.ceil(len / 4), Math.ceil(len / 2), Math.ceil((len * 3) / 4), len],
        tickFormatter: (v: number) => `${String(v)}日`,
      };
    }

    if (
      view === 'quarter' &&
      trend &&
      trend.quarterTrendDaily &&
      trend.quarterTrendDaily.length > 0
    ) {
      // Q 全体の日次系列。planned は各月 capacity を営業日按分した階段状、actual は
      // 完了=完了更新日 / 処理中=当日 の日次累積 (月 view と同ロジック)。
      const arr = trend.quarterTrendDaily.map((d, i) => {
        // 過去 Q は完了のみ (actualCompletedCumulative)、当 Q は合計 (actualCumulative)
        const actualVal = quarterCompletedOnly ? d.actualCompletedCumulative : d.actualCumulative;
        return {
          day: i + 1,
          date: d.date,
          planned: Math.round(d.plannedCumulative),
          actual: actualVal == null ? null : Math.round(actualVal),
        };
      });
      // dot/ReferenceLine 用の「今日」= 最後に actual が non-null な日
      let asOfDay = arr.length;
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i]?.actual != null) {
          asOfDay = i + 1;
          break;
        }
      }
      // X 軸 tick は各月の最初の日 (月ラベル)
      const seenMonths = new Set<string>();
      const ticks: number[] = [];
      for (const p of arr) {
        const ym = p.date.slice(0, 7);
        if (!seenMonths.has(ym)) {
          seenMonths.add(ym);
          ticks.push(p.day);
        }
      }
      return {
        data: arr,
        today: asOfDay,
        ticks,
        tickFormatter: (v: number) => {
          const d = arr[v - 1]?.date;
          return d ? `${String(parseInt(d.slice(5, 7), 10))}月` : String(v);
        },
      };
    }

    if (view === 'quarter' && trend && trend.quarterMonths.length > 0) {
      const currentMonth = asOfDate?.slice(0, 7);
      const currentIdx = (() => {
        if (!currentMonth) return trend.quarterMonths.length;
        const idx = trend.quarterMonths.findIndex((m) => m.month === currentMonth);
        return idx >= 0 ? idx + 1 : trend.quarterMonths.length;
      })();
      let plannedCum = 0;
      let actualCum = 0;
      const arr = trend.quarterMonths.map((m, i) => {
        plannedCum += m.capacity ?? 0;
        actualCum += m.totalActualCompleted + m.totalActualInProgress;
        return {
          day: i + 1,
          planned: Math.round(plannedCum),
          actual: i + 1 <= currentIdx ? Math.round(actualCum) : null,
        };
      });
      return {
        data: arr,
        today: currentIdx,
        ticks: arr.map((p) => p.day),
        tickFormatter: (v: number) => {
          const ms = trend.quarterMonths[v - 1]?.month;
          return ms ? `${String(parseInt(ms.slice(5), 10))}月` : String(v);
        },
      };
    }

    if (view === 'quarter') {
      const cfg = { currentIdx: 7, totalTarget: 2160, totalSteps: 13 };
      return {
        data: buildTrendData(cfg.currentIdx, cfg.totalTarget, cfg.totalSteps),
        today: cfg.currentIdx,
        ticks: [1, 4, 7, 10, 13],
        tickFormatter: (v: number) => `W${String(v)}`,
      };
    }

    const cfg = { currentIdx: 16, totalTarget: 720, totalSteps: 30 };
    return {
      data: buildTrendData(cfg.currentIdx, cfg.totalTarget, cfg.totalSteps),
      today: cfg.currentIdx,
      ticks: [1, 8, 15, 22, 30],
      tickFormatter: (v: number) => `${String(v)}日`,
    };
  }, [view, trend, asOfDate, quarterCompletedOnly]);

  // 目標線は予定からのみ導出する (目標 = 予定 × 1.05)。month / quarter / 過去期間の
  // どの分岐が組んだ系列にも同じ規則で付くよう、view 分岐の後で一括付与する。
  const chartData = useMemo(() => withTrendTarget(data), [data]);

  const config = { ticks, tickFormatter };

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="w-full flex-1 min-h-[240px]">
        <ResponsiveContainer>
          <ComposedChart data={chartData} margin={{ top: 24, right: 24, bottom: 4, left: -8 }}>
            <defs>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ACTUAL_COLOR} stopOpacity={0.14} />
                <stop offset="100%" stopColor={ACTUAL_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="0" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
              ticks={config.ticks}
              tickFormatter={config.tickFormatter}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            <RTooltip
              cursor={{ stroke: 'var(--color-border-hover)', strokeWidth: 1 }}
              contentStyle={{
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: 'var(--color-text-muted)' }}
              labelFormatter={(label) => config.tickFormatter(Number(label))}
              itemSorter={(item) => TOOLTIP_ORDER[String(item.dataKey)] ?? 99}
              formatter={(value, name) => [
                Number(value).toLocaleString(),
                t(SERIES_LABEL_KEY[String(name)] ?? 'idtKpi.teamTrend.actual'),
              ]}
            />
            <ReferenceLine
              x={today}
              stroke="var(--color-border-hover)"
              strokeDasharray="2 3"
              ifOverflow="extendDomain"
            />
            <Area
              type="monotone"
              dataKey="actual"
              stroke="none"
              fill="url(#actualGrad)"
              connectNulls={false}
              tooltipType="none"
              isAnimationActive
              animationDuration={1200}
              animationBegin={200}
              animationEasing="ease-out"
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke={TARGET_COLOR}
              strokeWidth={1.75}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive
              animationDuration={1000}
              animationBegin={100}
              animationEasing="ease-out"
            />
            <Line
              type="monotone"
              dataKey="planned"
              stroke={PLANNED_COLOR}
              strokeWidth={1.75}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive
              animationDuration={1000}
              animationBegin={100}
              animationEasing="ease-out"
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke={ACTUAL_COLOR}
              strokeWidth={2.25}
              dot={renderActualLatestDot(today)}
              activeDot={{
                r: 5,
                fill: 'var(--color-bg-surface)',
                stroke: ACTUAL_COLOR,
                strokeWidth: 2,
              }}
              connectNulls={false}
              isAnimationActive
              animationDuration={1200}
              animationBegin={400}
              animationEasing="ease-out"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-[var(--color-text-muted)]">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="2" aria-hidden="true">
            <line
              x1="0"
              y1="1"
              x2="20"
              y2="1"
              stroke={PLANNED_COLOR}
              strokeWidth={1.75}
              strokeDasharray="3,3"
            />
          </svg>
          {t('idtKpi.teamTrend.planned')}
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="20" height="2" aria-hidden="true">
            <line
              x1="0"
              y1="1"
              x2="20"
              y2="1"
              stroke={TARGET_COLOR}
              strokeWidth={1.75}
              strokeDasharray="5,3"
            />
          </svg>
          {t('idtKpi.teamTrend.target')}
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="20" height="2" aria-hidden="true">
            <line x1="0" y1="1" x2="20" y2="1" stroke={ACTUAL_COLOR} strokeWidth={2.25} />
          </svg>
          {t('idtKpi.teamTrend.actual')}
        </div>
        <div className="ml-auto">
          <TrendViewSwitch view={view} onChange={onViewChange} />
        </div>
      </div>
    </div>
  );
}
